import React, { useState } from 'react';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Option, Select, Spinner } from '@material-tailwind/react';
import moment from 'moment';
import { useGetSupportTrend } from '../../custom-hooks/use-support';
// import { useGetUsers } from '../../custom-hooks/use-users'; // Assigned agent filter
import { SUPPORT_TICKET_PRIORITIES_ENUM } from '../../utils/enums';
import { useGetSupportCategories } from '../../custom-hooks/use-support-categories';
import { convertToPresentableText } from '../../utils/functions';

const CREATED_COLOR = '#2a78d6';
const RESOLVED_COLOR = '#1baf7a';

const tooltipStyle = {
    borderRadius: 12,
    border: '1px solid #E6DDCF',
    fontSize: 12,
    boxShadow: '0 12px 24px -12px rgba(40,22,8,0.25)',
};

const RANGES = [
    { value: '7', label: '7 days' },
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
];

const GRANULARITIES = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
];

// Backend emits "2026-07-11", "2026-W28" or "2026-07" depending on granularity — only
// the daily form is a date moment can parse on its own. Mirrors the same helper in
// inventory/ReportsView.js.
const formatPeriod = (value, granularity, short) => {
    if (granularity === 'weekly') {
        const [year, week] = String(value).split('-W');
        return short ? `Wk ${week}` : `Week ${week}, ${year}`;
    }
    if (granularity === 'monthly') {
        return moment(value, 'YYYY-MM').format(short ? 'MMM' : 'MMMM YYYY');
    }
    return moment(value).format(short ? 'D MMM' : 'DD MMM YYYY');
};

const SupportTrendPanel = () => {
    const [days, setDays] = useState('30');
    const [granularity, setGranularity] = useState('daily');
    const [category, setCategory] = useState('');
    const [priority, setPriority] = useState('');
    const { data: categories = [] } = useGetSupportCategories();
    // const [agent, setAgent] = useState(''); // Assigned agent filter

    const { data, isLoading } = useGetSupportTrend({
        days,
        granularity,
        category: category || undefined,
        priority: priority || undefined,
        // agent: agent || undefined, // Assigned agent filter
    });

    // Assigned agent filter — the trend endpoint still accepts an `agent` param, so
    // re-enabling this needs nothing on the backend.
    // const { data: agentsData } = useGetUsers({
    //     role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE, STATIC_USER_ROLES.STAFF],
    //     _page: 1,
    //     _limit: 1000,
    // });
    // const agents = agentsData?.users || [];

    const activeGranularity = data?.granularity || granularity;
    const chartData = (data?.trend || []).map((row) => ({
        ...row,
        label: formatPeriod(row.period, activeGranularity, true),
        fullLabel: formatPeriod(row.period, activeGranularity, false),
    }));

    const hasAnyActivity = chartData.some((row) => row.created > 0 || row.resolved > 0);

    // A grid rather than a flex row: five fixed-width selects total ~865px, which
    // overflowed the panel (and gave the whole page a horizontal scrollbar) because a
    // flex row alongside the title can't shrink enough for flex-wrap to engage.
    //
    // Single column below `sm` on purpose — Material Tailwind's Select has a minimum
    // intrinsic width wider than half a 425px viewport, so a 2-column grid there makes
    // the boxes overlap and spill out of the panel.
    //
    // `selected` computes the closed-box label ourselves — Material Tailwind's own
    // display lookup for the closed Select is unreliable in this version.
    const filters = (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div><Select
                label="Category"
                value={category}
                onChange={(value) => setCategory(value || '')}
                selected={() => (category ? categories.find((c) => c.slug === category)?.name || category : 'All Categories')}
            >
                <Option value="">All Categories</Option>
                {categories.map((cat) => (
                    <Option key={cat.slug} value={cat.slug}>{cat.name}</Option>
                ))}
            </Select></div>

            <div><Select
                label="Priority"
                value={priority}
                onChange={(value) => setPriority(value || '')}
                selected={() => (priority ? convertToPresentableText(priority) : 'All Priorities')}
            >
                <Option value="">All Priorities</Option>
                {SUPPORT_TICKET_PRIORITIES_ENUM.map((p) => (
                    <Option key={p} value={p}>{convertToPresentableText(p)}</Option>
                ))}
            </Select></div>

            {/* <div><Select
                label="Assigned agent"
                value={agent}
                onChange={(value) => setAgent(value || '')}
                selected={() => (agent ? agents.find((a) => a._id === agent)?.name || '' : 'All Agents')}
            >
                <Option value="">All Agents</Option>
                {agents.map((a) => <Option key={a._id} value={a._id}>{a.name}</Option>)}
            </Select></div> */}

            <div><Select
                label="Range"
                value={days}
                onChange={(value) => setDays(value)}
                selected={() => RANGES.find((r) => r.value === days)?.label || ''}
            >
                {RANGES.map((r) => <Option key={r.value} value={r.value}>{r.label}</Option>)}
            </Select></div>

            <div><Select
                label="View by"
                value={granularity}
                onChange={(value) => setGranularity(value)}
                selected={() => GRANULARITIES.find((g) => g.value === granularity)?.label || ''}
            >
                {GRANULARITIES.map((g) => <Option key={g.value} value={g.value}>{g.label}</Option>)}
            </Select></div>
        </div>
    );

    return (
        <div className="rounded-2xl border border-inv-cream-200 bg-white p-4 sm:p-5">
            {/* Title above the filters, not beside them — five selects sharing a flex row
                with the heading left them too narrow to read at anything under ~1600px. */}
            <div className="mb-4">
                <h3 className="font-bold text-inv-ink">Ticket Trend</h3>
                <p className="text-xs text-inv-muted">
                    Created against resolved — when the lines pull apart, the queue is growing
                </p>
            </div>
            <div className="mb-5">{filters}</div>

            {isLoading ? (
                <div className="flex items-center justify-center" style={{ height: 280 }}>
                    <Spinner className="h-7 w-7 text-inv-brand" />
                </div>
            ) : !hasAnyActivity ? (
                <div className="flex items-center justify-center text-sm text-inv-muted-light" style={{ height: 280 }}>
                    No tickets created or resolved in this period
                </div>
            ) : (
                // Negative margin pulls the plot back over the panel's own padding on
                // mobile, where ~32px of side padding is a large share of the width.
                <div className="-mx-2 w-[calc(100%+1rem)] sm:mx-0 sm:w-full" style={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                            <CartesianGrid vertical={false} stroke="#e1e0d9" />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 11, fill: '#898781' }}
                                axisLine={{ stroke: '#c3c2b7' }}
                                tickLine={false}
                                minTickGap={16}
                            />
                            <YAxis
                                // Counts are small integers; Recharts' 60px default is mostly
                                // empty space, which matters on a narrow screen.
                                width={32}
                                tick={{ fontSize: 11, fill: '#898781' }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />
                            <Tooltip
                                contentStyle={tooltipStyle}
                                labelFormatter={(_label, payload) => payload?.[0]?.payload?.fullLabel || ''}
                            />
                            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                            <Line
                                type="monotone"
                                dataKey="created"
                                name="Created"
                                stroke={CREATED_COLOR}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="resolved"
                                name="Resolved"
                                stroke={RESOLVED_COLOR}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default SupportTrendPanel;
